import {
  ChangeEvent,
  DragEvent as ReactDragEvent,
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ControlItemValue } from '../../../types/types';
import SeptimLoadingSpinner from '../../../components/Manager/SeptimLoadingSpinner/SeptimLoadingSpinner';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { ExportColumnOptions, ExportSection } from '../types';
import ExportColumnItem from './ExportColumnItem';

interface ExportColumnsPanelProps {
  activeSection: ExportSection | undefined;
  isWithSubsetsMode: boolean;
  isLoadingActiveSection: boolean;
  isLoadingSubsetDefinition: boolean;
  onToggleColumn: (columnName: string, selected: boolean) => void;
  onColumnTextOptionChange: (
    columnName: string,
    optionName: keyof Pick<
      ExportColumnOptions,
      'prefix' | 'suffix' | 'maxLength' | 'decimalPlaces'
    >
  ) => (event: ChangeEvent<HTMLInputElement>) => void;
  onRoundingModeChange: (
    columnName: string
  ) => (value: ControlItemValue) => void;
  onReorderColumns: (
    sectionId: string,
    sourceColumnName: string,
    targetColumnName: string
  ) => void;
}

const ExportColumnsPanel: FC<ExportColumnsPanelProps> = ({
  activeSection,
  isWithSubsetsMode,
  isLoadingActiveSection,
  isLoadingSubsetDefinition,
  onToggleColumn,
  onColumnTextOptionChange,
  onRoundingModeChange,
  onReorderColumns,
}) => {
  const { t } = useTranslate();
  const [draggedColumnName, setDraggedColumnName] = useState<string | null>(
    null
  );
  const [activeColumnSettingsName, setActiveColumnSettingsName] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!draggedColumnName) {
      return undefined;
    }

    const previousDocumentOverscroll =
      document.documentElement.style.overscrollBehaviorY;
    const previousBodyOverscroll = document.body.style.overscrollBehaviorY;

    // Native pull-to-refresh belongs to the viewport, so lock html/body while dragging.
    document.documentElement.style.overscrollBehaviorY = 'none';
    document.body.style.overscrollBehaviorY = 'none';

    return () => {
      document.documentElement.style.overscrollBehaviorY =
        previousDocumentOverscroll;
      document.body.style.overscrollBehaviorY = previousBodyOverscroll;
    };
  }, [draggedColumnName]);

  const handleOpenColumnSettings = (columnName: string): void => {
    setActiveColumnSettingsName((current) =>
      current === columnName ? null : columnName
    );
  };

  const handleDragStart = (
    event: ReactDragEvent<HTMLDivElement>,
    columnName: string
  ): void => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', columnName);
    setDraggedColumnName(columnName);
  };

  const handleDragOver = (event: ReactDragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = useCallback(
    (event: ReactDragEvent<HTMLDivElement>, targetColumnName: string): void => {
      event.preventDefault();
      if (!activeSection || !draggedColumnName) return;
      onReorderColumns(activeSection.id, draggedColumnName, targetColumnName);
      setDraggedColumnName(null);
    },
    [activeSection, draggedColumnName, onReorderColumns]
  );

  const handleDragEnd = (): void => {
    setDraggedColumnName(null);
  };

  const isLoading = isLoadingActiveSection || isLoadingSubsetDefinition;

  return (
    <section className="dataset-export-columns">
      <h2>{activeSection?.label ?? t('export.columns')}</h2>
      {/* Mode-specific hint avoids subset text in main-only exports. */}
      <p>
        {isWithSubsetsMode
          ? t('export.prototypeDataScope')
          : t('export.mainDataScope')}
      </p>
      {isLoading && (
        <div className="dataset-export-definition-loading">
          <SeptimLoadingSpinner />
        </div>
      )}
      <div className="dataset-export-column-list">
        {activeSection?.columns.map((column) => (
          <ExportColumnItem
            key={column.name}
            column={column}
            isDragging={draggedColumnName === column.name}
            isSettingsOpen={activeColumnSettingsName === column.name}
            isSectionEnabled={activeSection.enabled}
            onToggleColumn={onToggleColumn}
            onOpenColumnSettings={handleOpenColumnSettings}
            onColumnTextOptionChange={onColumnTextOptionChange}
            onRoundingModeChange={onRoundingModeChange}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
          />
        ))}
        {activeSection?.columns.length === 0 && !isLoading && (
          <p className="dataset-export-empty">
            {t('export.noColumnsAvailable')}
          </p>
        )}
      </div>
    </section>
  );
};

export default ExportColumnsPanel;
